import { Dispatch, SetStateAction } from 'react';
import { Chart, ChartDataset, LegendItem } from 'chart.js';

import { TLabelType } from '@interfaces/common/common';
import { IChart, IChartPoint, ICompareSettings, ICorrelateSettings } from '@interfaces/kpi/kpiCommon';

import { EVendorsValues, TTimeAggregationTypes } from '@enums/common';
import { EAggregationTypes } from '@enums/kpi-api';
import { EChartTypes } from '@enums/kpi-common';

import { backgroundColorsBarChart, backgroundColorsLineChart, borderColors, colors } from '@constants/colors';
import {
	ANNOTATIONS_BORDER_WIDTH,
	COUNT_OF_COLORS,
	COUNT_OF_CORRELATE_CHARTS,
	FONT_SIZE_LEGEND,
	LABEL_SIZE_LEGEND,
	labelColorStyle,
	LEGEND_ALIGN,
	LEGEND_POSITION,
	MAX_COUNT_OF_COMPARE_CHARTS,
	MIN_BAR_LENGTH,
	MIN_POINTS_IN_CHART,
	RADIX,
	SUGGESTED_MAX,
	SUGGESTED_MIN,
} from '@constants/KPI/kpi-common';

import { checkIsMultiDays, dateFormattingKpi } from './date';
import { getLogarithmicLine } from './prepareChartData';

export const generateAnnotations = (thresholds: Array<string>) => {
	return thresholds.reduce((acc, threshold, currentIndex) => {
		const type = 'line';

		return {
			...acc,
			[`${type}${currentIndex + 1}`]: {
				type,
				yMin: threshold,
				yMax: threshold,
				borderColor: colors.pink50,
				borderWidth: ANNOTATIONS_BORDER_WIDTH,
			},
		};
	}, {});
};

export const generateTitleTooltip = ({
	timeAggregationType,
	organizationTimezone,
}: {
	charts: Array<IChart>;
	timeAggregationType: TTimeAggregationTypes;
	isSingleChart: boolean;
	organizationTimezone: string;
	isOranVendor: boolean;
}) => {
	return function (context: Array<{
		dataset: TLabelType & { data: IChartPoint[] };
		datasetIndex: number;
		dataIndex: number;
		label: string;
	}>) {
		const dataIndex = context[0].dataIndex;
		const currentListOfLabels: Array<number> = context[0].dataset.data.map(({ x }) => x);
		const isMultiDays = checkIsMultiDays({
			points: context[0].dataset.data,
			organizationTimezone,
		});

		return dateFormattingKpi({
			date: +currentListOfLabels[dataIndex],
			timeAggregationType: timeAggregationType,
			organizationTimezone,
			isMultiDays,
		});
	};
};

export const generateLabelTooltip = ({
	isLogarithmic = false,
}: {
	isLogarithmic?: boolean;
}) => {
	return function (context: {
		dataset: TLabelType & { data: IChartPoint[] & { isZeroValue: boolean }[] };
		raw: { isZeroValue: boolean };
		parsed: { y: string };
		dataIndex: number;
	}) {
		const isLineChart = context.dataset.type === 'line';
		const dataPoint = context.dataset.data[context.dataIndex];
		const labelValueY = context?.raw?.isZeroValue ? '0' : context.parsed.y;
		const label = ` ${labelValueY}${isLineChart ? `%` : ''}`;

		if (isLogarithmic && dataPoint?.isZeroValue) {
			return `${label}. Value adjusted for representation purposes`;
		}

		return label;
	};
};

export const prepareOptions = ({
	charts,
	timeAggregationType,
	isLegend,
	isThreshold,
	isLogarithmic,
	height,
	isAnimation,
	organizationTimezone,
	isMaintainAspectRatio,
	isAxisVisible = false,
	setVisibleAxes,
	visibleAxes,
	legendPlacement,
}: {
	charts: Array<IChart>;
	timeAggregationType: TTimeAggregationTypes;
	isLegend: boolean;
	isThreshold: boolean;
	isLogarithmic: boolean;
	height?: number;
	isMaintainAspectRatio?: boolean;
	isAnimation: boolean;
	organizationTimezone: string;
	showAxesTitles?: boolean;
	isAxisVisible?: boolean;
	setVisibleAxes?: Dispatch<SetStateAction<number[]>>;
	visibleAxes?: Array<number>;
	legendPlacement?: string;
}) => {
	const isOranVendor = charts[0]?.vendor.name === EVendorsValues.oran;
	const isSingleChart = charts.length === 1;
	const thresholds = charts.reduce<Array<string>>((acc, chart) => {
		return [...acc, ...chart.thresholds];
	}, []);
	const animation = isAnimation ? {} : {
		animation: {
			duration: 0,
		},
	};

	const transformDataForChartScales = (charts: any[]) => {
		const firstColor = backgroundColorsLineChart[0 % COUNT_OF_COLORS];
		const isMultiaxisCommonAxias = charts[0].lines.length > 1;
		const thresholdsCommonAxias = charts.flatMap(chart => chart.thresholds)
			.map(value => parseInt(value, RADIX))
			.filter(value => !isNaN(value));
		const currentListOfYCommonAxias: Array<string | null> =
			charts[0].lines[0].points.map(({ y }: { y: string | null }) => y);
		const yNumbersCommonAxias = currentListOfYCommonAxias
			.filter(item => item !== null)
			.map(item => parseInt(String(item), RADIX))
			.filter(item => !isNaN(item));
		const combinedArrayCommonAxias = [...yNumbersCommonAxias, ...thresholdsCommonAxias];
		const suggestedMinCommonAxias = Math.min(...combinedArrayCommonAxias);
		const suggestedMaxCommonAxias = Math.max(...combinedArrayCommonAxias);
		const isLineChartCommonAxias = charts[0].type.name === EChartTypes.line;
		const isUseDefaultSuggestedCommonAxias =
			isLineChartCommonAxias && (suggestedMinCommonAxias === SUGGESTED_MIN
				|| suggestedMaxCommonAxias === SUGGESTED_MIN);
		const hasPercentageCharts = charts.some(chart => chart.type.name === EChartTypes.line);
		const hasAbsoluteCharts = charts.some(chart => chart.type.name === EChartTypes.bar);

		const scales: any = {
			'x-axis-0': {
				type: 'category',
				position: 'bottom',
				ticks: {
					color: firstColor,
					callback: function (
						this: {
							chart: {
								data: {
									datasets: Array<{ data: Array<IChartPoint> }>;
								};
							};
						},
						_value: number,
						index: number,
					) {
						const firsttListOfLabels: Array<number> = charts[0].lines[0].points.map(({ x }: {
							x: number;
						}) => x);

						if (firsttListOfLabels[index] === undefined) return '';

						const chartInstance = this.chart;
						const points = chartInstance.data.datasets[0].data;

						const isMultiDays = checkIsMultiDays({
							points: points,
							organizationTimezone,
						});

						return dateFormattingKpi({
							date: +firsttListOfLabels[index],
							timeAggregationType: timeAggregationType,
							organizationTimezone,
							isMultiDays,
							showHourWithDayMonth: true,
						});
					},
				},
				grid: {
					borderColor: firstColor,
					tickColor: firstColor,
					color: firstColor,
					lineWidth: 0.5,
					padding: 10,
				},
			},
			'y-axis-percentage': {
				...isLogarithmic ? { type: 'logarithmic' } : { type: 'linear' },
				display: hasPercentageCharts ? !isAxisVisible : false,
				ticks: {
					color: firstColor,
					padding: 10,
					callback: function (value: string) {
						return `${value}%`;
					},
				},
				grid: {
					borderColor: firstColor,
					tickColor: firstColor,
					color: firstColor,
					lineWidth: 0.5,
				},
				...!isMultiaxisCommonAxias && isUseDefaultSuggestedCommonAxias
					? {
						suggestedMin: SUGGESTED_MIN,
						suggestedMax: SUGGESTED_MAX,
					} : {},
			},
			'y-axis-numeric': {
				display: hasAbsoluteCharts ? !isAxisVisible : false,
				...isLogarithmic ? { type: 'logarithmic' } : { type: 'linear' },
				ticks: {
					color: firstColor,
					padding: 10,
					callback: function (value: string) {
						return value;
					},
				},
				grid: {
					borderColor: firstColor,
					tickColor: firstColor,
					color: firstColor,
					lineWidth: 0.5,
				},
				...!isMultiaxisCommonAxias && isUseDefaultSuggestedCommonAxias
					? {
						suggestedMin: SUGGESTED_MIN,
						suggestedMax: SUGGESTED_MAX,
					} : {},
			},
		};

		charts.forEach((chart, chartIndex) => {
			const xAxisId = `x-axis-${chartIndex}`;
			const yAxisId = `y-axis-${chartIndex}`;
			const currentColor = backgroundColorsLineChart[chartIndex % COUNT_OF_COLORS];
			const isFirst = chartIndex === 0;
			const isMultiaxis = chart.lines.length > 1;
			const currentListOfY: Array<string | null> = chart.lines[0].points.map(({ y }: { y: string | null }) => y);
			const thresholds = charts.flatMap(chart => chart.thresholds)
				.map(value => parseInt(value, RADIX))
				.filter(value => !isNaN(value));
			const yNumbers = currentListOfY
				.filter(item => item !== null)
				.map(item => parseInt(String(item), RADIX))
				.filter(item => !isNaN(item));
			const combinedArray = [...yNumbers, ...thresholds];
			const currentListOfLabels: Array<number> = chart.lines[0].points.map(({ x }: { x: number }) => x);
			const suggestedMin = Math.min(...combinedArray);
			const suggestedMax = Math.max(...combinedArray);
			const isLineChart = chart.type.name === EChartTypes.line;
			const isUseDefaultSuggested = isLineChart
				&& (suggestedMin === SUGGESTED_MIN || suggestedMax === SUGGESTED_MIN);
			const isMultiDays = checkIsMultiDays({
				points: chart.lines[0].points,
				organizationTimezone,
			});

			if (chartIndex !== 0) {
				scales[xAxisId] = {
					type: 'category',
					position: 'bottom',
					display: isAxisVisible
						? visibleAxes?.includes(chartIndex) : false,
					grid: {
						borderColor: currentColor,
						tickColor: currentColor,
						color: isFirst ? currentColor : 'transparent',
						lineWidth: 0.5,
						drawOnChartArea: false,
					},
					ticks: {
						color: currentColor,
						callback: function (_value: number, index: number) {
							if (currentListOfLabels[index] === undefined) return '';

							return dateFormattingKpi({
								date: +currentListOfLabels[index],
								timeAggregationType: timeAggregationType,
								organizationTimezone,
								isMultiDays,
								showHourWithDayMonth: true,
							});
						},
					},
				};
			}
			scales[yAxisId] = {
				...isLogarithmic ? { type: 'logarithmic' } : { type: 'linear' },
				position: 'left',
				display: isAxisVisible
					? visibleAxes?.includes(chartIndex) : false,
				ticks: {
					color: currentColor,
					padding: 10,
					callback: function (value: string) {
						return isLineChart ? `${value}%` : value;
					},
				},
				grid: {
					borderColor: currentColor,
					tickColor: currentColor,
					color: isFirst ? currentColor : 'transparent',
					lineWidth: 0.5,
				},
				...!isMultiaxis && isUseDefaultSuggested
					? {
						suggestedMin: SUGGESTED_MIN,
						suggestedMax: SUGGESTED_MAX,
					} : {},

			};
		});

		return { scales };
	};

	return ({
		maintainAspectRatio: isMaintainAspectRatio ?? !height,
		responsive: true,
		...animation,
		plugins: {
			annotation: {
				annotations: isThreshold ? generateAnnotations(thresholds) : {},
			},
			legend: {
				display: isLegend,
				position: legendPlacement || LEGEND_POSITION,
				align: LEGEND_ALIGN,
				fullSize: true,
				labels: {
					color: colors.indigo70,
					boxHeight: LABEL_SIZE_LEGEND,
					boxWidth: LABEL_SIZE_LEGEND,
					font: {
						size: FONT_SIZE_LEGEND,
					},
				},
				onClick: (_e: MouseEvent, legendItem: LegendItem, legend: { chart: Chart }) => {
					const chart = legend.chart;

					if (legendItem.datasetIndex === undefined) {
						return;
					}

					const datasetIndex = legendItem.datasetIndex;

					const yAxisId = `y-axis-${datasetIndex}`;
					const xAxisId = `x-axis-${datasetIndex}`;

					const meta = chart.getDatasetMeta(datasetIndex);

					if (!meta) {
						return;
					}

					meta.hidden = !meta.hidden;

					const yAxis = chart.options.scales?.[yAxisId];
					const xAxis = chart.options.scales?.[xAxisId];

					if (yAxis && xAxis) {
						yAxis.display = !meta.hidden;
						xAxis.display = !meta.hidden;
					}

					if (setVisibleAxes) {
						setVisibleAxes((prevState) => {
							if (meta.hidden) {
								return prevState.filter((axis: number) => axis !== datasetIndex);
							}
							return [...new Set([...prevState, datasetIndex])];
						});
					}

					chart.update();
				},
			},
			tooltip: {
				backgroundColor: colors.indigoTransparent_90,
				callbacks: {
					title: generateTitleTooltip({
						charts,
						timeAggregationType,
						isSingleChart,
						organizationTimezone,
						isOranVendor,
					}),
					label: generateLabelTooltip({
						isLogarithmic,
					}),
					labelColor: (context: { dataset: { borderColor?: string; backgroundColor: string } }) => ({
						borderColor: context.dataset.borderColor || context.dataset.backgroundColor,
						backgroundColor: context.dataset.backgroundColor,
						...labelColorStyle,
					}),
				},
			},
		},
		scales: transformDataForChartScales(charts).scales,
		elements: {
			point: {
				radius: 4,
				hoverRadius: 6,
			},
		},
	});
};

export const prepareData = (
	charts: Array<IChart>,
	customColorIndex?: number,
	isAxisVisible: boolean = false,
	isLogarithmic: boolean = false,
) => {

	const transformDataForChart = (charts: Array<IChart>) => {
		const datasets: ChartDataset<'line' | 'bar'>[] = [];

		charts.forEach((chart, chartIndex) => {
			const yAxisId = `y-axis-${chartIndex}`;

			const isLineChart = chart.type.name === EChartTypes.line;
			const currentColorIndex = customColorIndex || (chartIndex % COUNT_OF_COLORS);
			const isMultiaxis = chart.lines.length > 1;

			chart.lines.forEach((line: any, lineIndex) => {
				const colorIndexForLine = lineIndex % COUNT_OF_COLORS;

				const data = isLogarithmic
					? line.points.map(getLogarithmicLine)
					: line.points;

				const getPointBackgroundColor = (context: any) => {
					if (!isLogarithmic) {
						return isLineChart
							? backgroundColorsLineChart[isMultiaxis ? colorIndexForLine : currentColorIndex]
							: backgroundColorsBarChart[isMultiaxis ? colorIndexForLine : currentColorIndex];
					}

					const dataPoint = data[context.dataIndex];

					if (dataPoint?.isZeroValue) {
						return colors.gray50;
					}

					return isLineChart
						? backgroundColorsLineChart[isMultiaxis ? colorIndexForLine : currentColorIndex]
						: backgroundColorsBarChart[isMultiaxis ? colorIndexForLine : currentColorIndex];
				};

				const getPointBorderColor = (context: any) => {
					if (!isLogarithmic) {
						return borderColors[isMultiaxis ? colorIndexForLine : currentColorIndex];
					}

					const dataPoint = data[context.dataIndex];

					return dataPoint?.isZeroValue
						? colors.gray50
						: borderColors[isMultiaxis ? colorIndexForLine : currentColorIndex];
				};

				datasets.push({
					label: chart.aggregationType.name === EAggregationTypes.siteLevelAggregatedCells
						? line.name
						: chart.name,
					data,
					minBarLength: MIN_BAR_LENGTH,
					type: chart.type.name === EChartTypes.bar ? 'bar' : 'line',
					pointStyle: 'circle',
					backgroundColor: isLogarithmic
						? getPointBackgroundColor
						: (isLineChart
							? backgroundColorsLineChart[isMultiaxis ? colorIndexForLine : currentColorIndex]
							: backgroundColorsBarChart[isMultiaxis ? colorIndexForLine : currentColorIndex]),
					borderColor: isLineChart
						? (isLogarithmic
							? getPointBorderColor
							: borderColors[isMultiaxis ? colorIndexForLine : currentColorIndex])
						: 'none',
					xAxisID: 'x-axis-0',
					yAxisID: isAxisVisible
						? yAxisId
						: isLineChart ? 'y-axis-percentage' : 'y-axis-numeric',
					pointBackgroundColor: isLogarithmic && isLineChart ? getPointBackgroundColor : undefined,
					pointBorderColor: isLogarithmic && isLineChart ? getPointBorderColor : undefined,
				});
			});
		});

		return { datasets };
	};

	return {
		labels: charts[0]?.lines[0]?.points,
		datasets: transformDataForChart(charts).datasets,
	};
};

export const generateListOfCompareMessages = ({
	compareCharts,
	chart,
	compareSettings,
	isEmptyChart,
}: {
	compareCharts: Array<IChart>;
	chart: IChart;
	compareSettings: ICompareSettings;
	isEmptyChart: boolean;
}) => {
	const listOfMessages = [];

	if (compareCharts.length) {
		if (compareSettings.timeCount !== chart.lines[0].points.length) listOfMessages.push('the number of time points in the range differs from the points of charts in comparison');
		if (compareSettings.chartsCount === MAX_COUNT_OF_COMPARE_CHARTS) listOfMessages.push('up to 10 charts can be added to compare');
	}

	if (!compareSettings.aggregationTypes.includes(chart.aggregationType.name)) listOfMessages.push('the chart should have aggregated level of displaying data');
	if (isEmptyChart) listOfMessages.push('no data available');

	return listOfMessages;
};

export const generateListOfCorrelateMessages = ({
	correlateCharts,
	chart,
	correlateSettings,
	isEmptyChart,
}: {
	correlateCharts: Array<IChart>;
	chart: IChart;
	correlateSettings: ICorrelateSettings;
	isEmptyChart: boolean;
}) => {
	const listOfMessages = [];
	const availableValues = chart.lines[0].points.filter(point => point.y !== null);

	if (correlateCharts.length) {
		if (correlateSettings.timeCount !== availableValues.length) listOfMessages.push('the chart must be built for the same number of timestamps as the chart already added in the correlation');
		if (correlateSettings.chartsCount === COUNT_OF_CORRELATE_CHARTS) listOfMessages.push('2 charts should be added for correlation');
	}

	if (availableValues.every(value => value.y === availableValues[0].y)) listOfMessages.push('at least 2 values in the chart should differ from each other');
	if (availableValues.length < MIN_POINTS_IN_CHART) listOfMessages.push('the chart should contain minimum 3 values');
	if (!correlateSettings.aggregationTypes.includes(chart.aggregationType.name)) listOfMessages.push('the chart should have aggregated level of displaying data');
	if (isEmptyChart) listOfMessages.push('no data available');

	return listOfMessages;
};

export const checkIsEmptyChart = (chart?: IChart) =>
	!!chart?.lines.every(line => line.points.every(({ y }) => y === null));
